import {
  SimpleGrid,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
} from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import RadioInput from 'components/fields/home/RadioInput';
import SelectInput from 'components/fields/home/SelectInput';

import getRadioYesNo from 'helpers/getRadioYesNo';

import { changeDwellingRatingInformation } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';
import Divider from 'views/home_form/components/Divider';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface RatingInfoExtensionsProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const RatingInfoExtensions = ({
  dwelling,
  index,
}: RatingInfoExtensionsProps) => {
  const dispatch = useAppDispatch();
  const isBaraEnabled = useFeatureFlag('enable-bara');

  const ratingInfo = dwelling.rating_information;

  // Check if auxiliary heating professionally installed is mandatory from the quote
  const isAuxHeatingProfessionallyInstalledMandatory =
    ratingInfo?._info?.is_aux_heating_professionally_installed_mandatory ??
    false;

  if (!ratingInfo) return null;

  const onRadioChange = (
    value: string,
    systemType: 'primary' | 'auxiliary',
    field: string,
  ) => {
    const fieldName =
      systemType === 'primary'
        ? 'primary_heating_type'
        : 'auxiliary_heating_type';

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: fieldName,
        value: {
          ...ratingInfo[fieldName],
          [field]: value === 'true',
        },
      }),
    );
  };

  const onWaterHeaterChange = (event: any, field: string) => {
    const { value } = event.target;

    dispatch(
      changeDwellingRatingInformation({
        index,
        name: 'water_heater_type',
        value: {
          ...dwelling.rating_information.water_heater_type,
          [field]: value,
        },
      }),
    );
  };

  return (
    <Box id="sectionRatingInfoExtensions" mt="20px" mb="20px">
      <Divider mb="15px">Rating Information mandatory fields</Divider>

      <SimpleGrid columns={6} spacing="15px">
        <Box>
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Primary Heating System
          </Text>
          <RadioInput
            section={SECTIONS.DWELLINGS}
            label="Professionally Installed"
            subLabel="Primary"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={getRadioYesNo(
              ratingInfo.primary_heating_type.is_professionally_installed,
            )}
            onChange={(value: string) =>
              onRadioChange(value, 'primary', 'is_professionally_installed')
            }
            stacked
            required
          />
          {ratingInfo.primary_heating_type.is_professionally_installed ===
            false && (
            <Alert status="error" mt="10px" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <AlertDescription>
                The heating system is not professionally installed. Please refer
                to BIG Underwriting
                {isBaraEnabled
                  ? ' or submit an After-Hours Referral (MP/Delegate)'
                  : ''}
                .
              </AlertDescription>
            </Alert>
          )}
        </Box>
        <Box>
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Auxiliary Heating System
          </Text>
          <RadioInput
            section={SECTIONS.DWELLINGS}
            label="Professionally Installed"
            subLabel="Auxiliary"
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            value={getRadioYesNo(
              ratingInfo.auxiliary_heating_type.is_professionally_installed,
            )}
            onChange={(value: string) =>
              onRadioChange(value, 'auxiliary', 'is_professionally_installed')
            }
            stacked
            required={isAuxHeatingProfessionallyInstalledMandatory}
          />
          {ratingInfo.auxiliary_heating_type.is_professionally_installed ===
            false && (
            <Alert status="error" mt="10px" borderRadius="md" fontSize="sm">
              <AlertIcon />
              <AlertDescription>
                The heating system is not professionally installed. Please refer
                to BIG Underwriting
                {isBaraEnabled
                  ? ' or submit an After-Hours Referral (MP/Delegate)'
                  : ''}
                .
              </AlertDescription>
            </Alert>
          )}
        </Box>
        <Box>
          <Text fontWeight="medium" fontSize="md" mb="10px" color="gray.700">
            Water Heater
          </Text>
          <SelectInput
            section={SECTIONS.DWELLINGS}
            label="Water Heater Type"
            name="apparatus"
            value={ratingInfo.water_heater_type.apparatus || ''}
            onChange={(e: any) => onWaterHeaterChange(e, 'apparatus')}
            options={[
              { value: '', label: 'Select Type' },
              { value: 'Tank', label: 'Tank' },
              { value: 'Tankless', label: 'Tankless' },
            ]}
            required
          />
        </Box>
      </SimpleGrid>
    </Box>
  );
};

export default RatingInfoExtensions;
